import { apiClient } from '../client';
import { tokenStore } from '../../auth/tokenStore';
import { DEV_DEFAULTS } from '../../config/devDefaults';
import { authEndpoints } from '../endpoints/auth.endpoints';
import { toApiError } from './unwrap';
import type {
  AccessibleApplication,
  AccessibleTenant,
  AuthenticateRequest,
  AuthenticateResponse,
  DiscoverRequest,
  DiscoverResponse,
  LoginRequest,
  RolePermissionMerged,
  TenantHubApiEnvelope,
} from '../types/auth.types';
import type { ApiResponse } from '../types/envelope.types';
import { ORDERS_MODULE, type WaiterSession } from '../../auth/session.types';

/*
 * Login pipeline — direct against crs-api, exactly as crs-web does:
 *
 *   1. POST /api/v1/Auth/discover   { username, password, clientId = appId }
 *   2. Pick tenant + app: auto when unambiguous, else throw
 *      { code: 'DISCOVER_MULTIPLE', discovery } for the LoginScreen picker
 *   3. POST /api/v1/Auth/authenticate { username, password, selectedTenantId, selectedApplicationId }
 *   4. GET  /api/v1/RolePermissions/by-roles?roles=…  (roles from the JWT)
 *   5. Fail closed: no `dineops-orders` in visibleModules → refused
 *
 * No hardcoded fallback modules, no bootstrap synthesis.
 */

async function discover(req: DiscoverRequest): Promise<DiscoverResponse> {
  try {
    const res = await apiClient.post<TenantHubApiEnvelope<DiscoverResponse>>(
      authEndpoints.discover(),
      req,
    );
    const body = res.data;
    if (!body.success || !body.data) {
      throw new Error(body.message || 'Sign in failed — no tenants available');
    }
    return body.data;
  } catch (err) {
    throw toApiError(err, 'Sign in failed');
  }
}

async function authenticate(req: AuthenticateRequest): Promise<AuthenticateResponse> {
  try {
    const res = await apiClient.post<TenantHubApiEnvelope<AuthenticateResponse>>(
      authEndpoints.authenticate(),
      req,
    );
    const body = res.data;
    if (!body.success || !body.data) {
      throw new Error(body.message || 'Authentication failed');
    }
    return body.data;
  } catch (err) {
    throw toApiError(err, 'Authentication failed');
  }
}

async function fetchRolePermissions(
  roles: string[],
  tenantId: string | null,
): Promise<RolePermissionMerged> {
  if (roles.length === 0) return { visibleModules: [], moduleActions: {} };
  try {
    const csv = roles.join(',');
    const res = await apiClient.get<ApiResponse<RolePermissionMerged>>(
      `${authEndpoints.rolePermissionsByRoles()}?roles=${encodeURIComponent(csv)}`,
      { headers: { 'X-Tenant-ID': tenantId || DEV_DEFAULTS.tenantId } },
    );
    const body = res.data;
    if (!body.success || !body.data) {
      throw new Error(body.message || 'Role permissions lookup failed');
    }
    return {
      visibleModules: body.data.visibleModules ?? [],
      moduleActions: body.data.moduleActions ?? {},
    };
  } catch (err) {
    throw toApiError(err, 'Role permissions lookup failed');
  }
}

/**
 * Bare role names TenantHub returns in `userRole` (system, admin, …) are
 * normalised to their `crs_`-prefixed seed codes, which is what
 * `admin.tbl_role_permissions` stores. Sending the bare form matches nothing.
 */
const CRS_ROLE_ALIASES: Record<string, string> = {
  system: 'crs_system',
  admin: 'crs_admin',
  booking_agent: 'crs_booking_agent',
  bookingagent: 'crs_booking_agent',
  user: 'crs_user',
};

/**
 * Roles a JWT can carry: `roles[]`, `role`, `realm_access.roles[]`,
 * `resource_access.<any>.roles[]`, plus the TenantHub `userRole` fallback.
 * Every value is also added in its `crs_*` alias form when one exists.
 */
export function extractRoles(accessToken: string, fallback: string | null): string[] {
  const collected = new Set<string>();
  const add = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    collected.add(t);
    const alias = CRS_ROLE_ALIASES[t.toLowerCase()];
    if (alias) collected.add(alias);
  };

  if (fallback) add(fallback);
  try {
    const [, payload] = accessToken.split('.');
    if (!payload) return Array.from(collected);
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=')));
    const push = (v: unknown) => {
      if (typeof v === 'string' && v.trim()) add(v);
      else if (Array.isArray(v)) v.forEach(push);
    };
    push(json.role);
    push(json.roles);
    push(json?.realm_access?.roles);
    if (json?.resource_access && typeof json.resource_access === 'object') {
      for (const value of Object.values(json.resource_access as Record<string, unknown>)) {
        push((value as { roles?: unknown })?.roles);
      }
    }
  } catch {
    // JWT unreadable — fall back to whatever was passed in.
  }
  return Array.from(collected);
}

/** Only tenant→application combinations that expose at least one application. */
function pickAutoSelection(
  tenants: AccessibleTenant[],
): { tenant: AccessibleTenant; application: AccessibleApplication } | null {
  const eligible = tenants.filter((t) => t.applications.length > 0);
  if (eligible.length !== 1) return null;
  const [tenant] = eligible;
  if (tenant.applications.length !== 1) return null;
  return { tenant, application: tenant.applications[0] };
}

function assembleSession(auth: AuthenticateResponse, perms: RolePermissionMerged): WaiterSession {
  const fullName = [auth.userFirstName, auth.userMiddleName, auth.userLastName]
    .filter((s) => s && s.trim().length > 0)
    .join(' ')
    .trim();

  return {
    user: {
      userId: auth.userId ?? auth.userName ?? 'unknown',
      username: auth.userName ?? '',
      fullName: fullName || auth.userName || '',
      email: auth.userEmail ?? null,
      roles: auth.userRole ? [auth.userRole] : [],
    },
    tenantId: auth.tenantId ?? tokenStore.getTenantId() ?? '',
    tenantName: auth.tenantDisplayName || auth.tenantName || auth.tenantCode || '',
    modules: perms.visibleModules,
    actions: perms.moduleActions,
  };
}

export const authService = {
  /**
   * Runs the full discover → authenticate → by-roles pipeline and returns the
   * WaiterSession. Stores the access token, refresh token and tenant id in
   * tokenStore on success; stores nothing when the fail-closed check refuses.
   */
  async login(req: LoginRequest): Promise<WaiterSession> {
    const appId = tokenStore.getAppId() ?? DEV_DEFAULTS.appId;

    // Step 1 — discover.
    const discovery = await discover({
      username: req.username,
      password: req.password,
      tenantId: req.tenantId,
      clientId: appId,
    });

    if (!discovery.accessibleTenants || discovery.accessibleTenants.length === 0) {
      throw new Error('Your account has no accessible tenants. Please contact your administrator.');
    }

    // Step 2 — pick tenant + application.
    let selectedTenantId = req.tenantId ?? null;
    let selectedApplicationId = req.applicationId ?? null;

    if (!selectedTenantId || !selectedApplicationId) {
      const auto = pickAutoSelection(discovery.accessibleTenants);
      if (auto) {
        selectedTenantId = auto.tenant.tenantId;
        selectedApplicationId = auto.application.applicationId;
      } else if (
        discovery.recommendedTenant &&
        discovery.recommendedTenant.applications.length === 1
      ) {
        selectedTenantId = discovery.recommendedTenant.tenantId;
        selectedApplicationId = discovery.recommendedTenant.applications[0].applicationId;
      } else {
        throw Object.assign(new Error('Please select a tenant and application to continue.'), {
          code: 'DISCOVER_MULTIPLE',
          discovery,
        });
      }
    }

    // Step 3 — authenticate.
    const auth = await authenticate({
      username: req.username,
      password: req.password,
      selectedTenantId,
      selectedApplicationId,
    });

    // Step 4 — DB-driven role permissions (anonymous endpoint).
    const roles = extractRoles(auth.accessToken, auth.userRole ?? null);
    const perms = await fetchRolePermissions(roles, auth.tenantId ?? selectedTenantId);

    // Step 5 — fail closed on the waiter module.
    if (!perms.visibleModules.includes(ORDERS_MODULE)) {
      // Name the CRS role(s) the token carries (e.g. crs_cashier), not TenantHub's generic
      // userRole ("user"): that is what an administrator changes in Role Permissions.
      const crsRoles = roles.filter((r) => /^crs_/i.test(r));
      const specific = crsRoles.filter((r) => r.toLowerCase() !== 'crs_user');
      const displayRole = (specific.length ? specific : crsRoles).join(', ') || auth.userRole || roles.join(', ') || 'unknown';
      throw new Error(
        perms.visibleModules.length === 0
          ? `Your role "${displayRole}" is not registered in the CRS role table. ` +
            `Ask an administrator to grant the ${ORDERS_MODULE} module.`
          : `Your role "${displayRole}" has no access to the waiter app ` +
            `(${ORDERS_MODULE} is not enabled). Ask an administrator.`,
      );
    }

    // Step 6 — persist tokens + tenant context. X-App-ID stays pinned to the
    // env-configured client ID: TenantHub's `applicationId` can be a
    // different GUID and letting it drift breaks later requests.
    tokenStore.setTenantId(auth.tenantId ?? selectedTenantId);
    tokenStore.setAccessToken(auth.accessToken);
    if (auth.refreshToken) tokenStore.setRefreshToken(auth.refreshToken);

    return assembleSession(auth, perms);
  },

  /** Best-effort server logout; local cleanup is the SessionProvider's job. */
  async logout(): Promise<void> {
    try {
      const refreshToken = tokenStore.getRefreshToken();
      await apiClient.post(authEndpoints.logout(), refreshToken ? { refreshToken } : {});
    } catch {
      // ignore — tokens are cleared regardless
    }
  },
};
